import React, {Dispatch, SetStateAction, useContext, useEffect, useMemo, useState} from 'react'
import AppContext from '../../appContext'
import Dialog from '../../components/Dialog'
import QCMapPanel from '../../components/QCMapPanel'
import {SamplePoint} from '../../components/GeoMap'
import SampleDataOverlay from '../../components/SampleDataOverlay'
import {RowWithId} from './limeSoilUtils'
import {useBoundaries} from '../../controllers/useBoundaries'

interface SampleLocationsDialogProps {
    show: boolean
    setShow: Dispatch<SetStateAction<boolean>>
    rows: RowWithId[]
    samplePoints: SamplePoint[]
    landIds?: number[]
    multipleBoundaries?: Array<Array<{x: number; y: number}>>
    boundaryLoading?: boolean
    boundaryError?: boolean
    meq100gMode: boolean
}

const SampleLocationsDialog: React.FC<SampleLocationsDialogProps> = (props) => {
    const app = useContext(AppContext)
    const system = app.initial.system
    const [selectedLabel, setSelectedLabel] = useState<string | null>(null)

    // Only fetch boundaries when the dialog is open
    const activeLandIds = props.show ? props.landIds : undefined
    const {boundaryMap, loading: fetchLoading, error: fetchError} = useBoundaries(activeLandIds)
    const fetchedBoundaries = useMemo(() => Object.values(boundaryMap).filter(c => c.length > 0), [boundaryMap])

    // Use pre-passed boundaries if available, otherwise use fetched ones
    const multipleBoundaries = props.multipleBoundaries ?? fetchedBoundaries
    const boundaryLoading = props.boundaryLoading ?? fetchLoading
    const boundaryError = props.boundaryError ?? fetchError

    useEffect(() => {
        if (!props.show) setSelectedLabel(null)
    }, [props.show])

    const selectedRow = selectedLabel ? props.rows.find(r => (r.referenceNr || r.landRef) === selectedLabel) : null

    return (
        <Dialog
            title={app.word('sample_locations')}
            show={props.show}
            setShow={props.setShow}
            body={
                <div className="p-4 min-w-[70vw]">
                    {boundaryLoading ? (
                        <div className="flex items-center justify-center h-[60vh] bg-gray-100 rounded">
                            <div className="text-gray-500">{app.word('loading')}...</div>
                        </div>
                    ) : (
                        <QCMapPanel
                            samplePoints={props.samplePoints}
                            multipleBoundaries={multipleBoundaries}
                            boundaryError={boundaryError}
                            selectedPointLabel={selectedLabel}
                            onPointSelect={setSelectedLabel}
                        >
                            {selectedRow && (
                                <SampleDataOverlay
                                    row={selectedRow}
                                    meq100gMode={props.meq100gMode}
                                    system={system}
                                />
                            )}
                        </QCMapPanel>
                    )}
                    <div className="flex justify-end">
                        <button
                            className="btn-sm bg-primary"
                            onClick={() => props.setShow(false)}
                        >
                            {app.word('done')}
                        </button>
                    </div>
                </div>
            }
        />
    )
}

export default SampleLocationsDialog
